"use client";

import Image from "next/image";
import playIcon from "@/assets/svgs/play.svg";
import { CLink } from "@/components/common/customLink/CustomLink";
import LazyLoadedVideo from "@/components/common/LazyLoadedVideo";
import VisibilitySensor from "@/components/common/VisibilitySensor";
import ExpandableLineClampContent, {
  PortableTextValue,
} from "@/components/common/ExpandableLineClampContent";
import PortableText from "@/components/PortableText";
import { TranslationData } from "@/common/lib/types";
import { PortableTextBlock } from "next-sanity";
import { cn } from "@/lib/tailwind";
import {
  DetailedHTMLProps,
  ElementRef,
  HTMLAttributes,
  MouseEventHandler,
  ReactEventHandler,
  useRef,
  useState,
  VideoHTMLAttributes,
} from "react";

export type PromotionalVideoCardAction = {
  cta?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  subDescription?: React.ReactNode;
};

export type PromotionalVideoCardProps = {
  videoProps: DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement>;
  containerProps?: Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    "children"
  >;
  controlsProps?: Partial<{ onClick: () => void; className: string }>;
  action?: PromotionalVideoCardAction;
  showControls?: boolean;
  url?: string;
  onClick?: () => void;
  onVisible?: (isVisible: boolean) => void;
  translations?: TranslationData;
  subDescriptionLineClamp?: number;
  linkStyle?: { backgroundColor?: string; color?: string };
  contentBgColor?: string;
};

export default function PromotionalVideoCard({
  action,
  containerProps = {},
  controlsProps,
  videoProps,
  showControls,
  url,
  onClick,
  onVisible,
  translations,
  subDescriptionLineClamp = 6,
  linkStyle,
  contentBgColor = "#F4F4F4",
}: PromotionalVideoCardProps) {
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<ElementRef<"video">>(null);

  const handleTogglePlay: MouseEventHandler<HTMLDivElement> = (e) => {
    e?.stopPropagation();
    if (!videoRef?.current) return;
    const state = !isPlaying;
    setIsPlaying(state);
    if (state) videoRef?.current?.play();
    else videoRef?.current?.pause();
    controlsProps?.onClick?.();
  };

  const handleEnded: ReactEventHandler<HTMLVideoElement> = (e) => {
    if (videoRef?.current) videoRef.current.currentTime = 0;
    setIsPlaying(false);
    videoProps?.onEnded?.(e);
  };

  const videoBlock = (
    <div
      className="relative h-full w-full"
      {...(showControls && { onClick: handleTogglePlay })}
    >
      {url && !showControls ? (
        <CLink href={url}>
          <LazyLoadedVideo
            width="100%"
            height="100%"
            videoRef={videoRef}
            {...videoProps}
            onEnded={handleEnded}
            className={cn(videoProps?.className)}
          />
        </CLink>
      ) : (
        <LazyLoadedVideo
          width="100%"
          height="100%"
          videoRef={videoRef}
          {...videoProps}
          onEnded={handleEnded}
          className={cn(videoProps?.className)}
        />
      )}
      {!isPlaying && showControls && (
        <Image
          width={73}
          height={90}
          src={playIcon?.src}
          onClick={handleTogglePlay}
          alt="play"
          className={cn(
            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform",
            controlsProps?.className,
          )}
        />
      )}
    </div>
  );

  const hasRightSection = action?.subDescription;

  return (
    <div
      {...containerProps}
      className={cn("flex h-full w-full flex-col", containerProps?.className)}
      onClick={onClick}
    >
      {videoBlock}

      {action && (
        <div
          className={cn(
            "grid gap-6 px-4 py-6 md:px-6 md:py-8",
            hasRightSection ? "md:grid-cols-2" : "grid-cols-1",
          )}
          style={{ backgroundColor: contentBgColor }}
        >
          <div className="flex flex-col">
            {action?.title &&
              (typeof action.title === "string" ? (
                <h2 className="mb-1 text-[0.875rem] font-bold uppercase leading-tight text-black md:mb-2 lg:text-[32px]">
                  {action.title}
                </h2>
              ) : (
                action.title
              ))}
            {action?.description && (
              <div className="pt-2 text-sm text-black lg:text-lg">
                <PortableText value={action.description as unknown as PortableTextBlock} />
              </div>
            )}
            {hasRightSection && (
              <div className="md:hidden">
                <ExpandableLineClampContent
                  portableTextValue={action.subDescription as unknown as PortableTextValue}
                  translations={translations}
                  lineClamp={subDescriptionLineClamp}
                  className="pt-2 text-sm text-black"
                />
              </div>
            )}
            {action?.cta && (
              <div className="mt-3 sm:mt-4">
                {typeof action?.cta === "string" ? <p>{action?.cta}</p> : action?.cta}
              </div>
            )}
          </div>

          {hasRightSection && (
            <div className="hidden md:block">
              <ExpandableLineClampContent
                portableTextValue={action.subDescription as unknown as PortableTextValue}
                translations={translations}
                lineClamp={subDescriptionLineClamp}
                className="text-sm text-black md:text-base"
              />
            </div>
          )}
        </div>
      )}

      {onVisible && (
        <div className="w-full">
          <VisibilitySensor onVisible={onVisible}>
            <div />
          </VisibilitySensor>
        </div>
      )}
    </div>
  );
}
